import type { EmbedData } from "@repo/fika-blog/src/nodes/embed/embed-node"
import {
  EmbedNode,
  hasRichEmbed,
  parseEmbedUrl,
  renderEditorPreview,
  toEmbedData,
} from "@repo/fika-blog/src/nodes/embed/embed-node"
import type { NodeViewRendererProps } from "@repo/fika-blog/src/tiptap/core"
import { isNodeEmpty } from "@repo/fika-blog/src/tiptap/core"
import type { Node } from "@repo/fika-blog/src/tiptap/pm/model"
import { Plugin, PluginKey } from "@repo/fika-blog/src/tiptap/pm/state"
import type { EditorView } from "@repo/fika-blog/src/tiptap/pm/view"
import { urqlClient } from "@/context/graphql-provider"
import { graphql } from "@/graphql"
import "@repo/fika-blog/src/nodes/embed/embed-node.css"

const OEMBED_QUERY = graphql(`
  query OEmbedEnrich($url: String!) {
    oembed(url: $url) {
      url
      metadata {
        title
        description
        imageUrl
        faviconUrl
      }
      oembed {
        type
        providerName
        embedUrl
        embedHtml
        embedWidth
        embedHeight
        thumbnailUrl
        thumbnailWidth
        thumbnailHeight
      }
    }
  }
`)

async function enrichEmbedUrl(url: string): Promise<Partial<EmbedData> | null> {
  try {
    const result = await urqlClient.query(OEMBED_QUERY, { url }).toPromise()
    const data = result.data?.oembed
    if (!data) return null
    return {
      url: data.url,
      title: data.metadata.title,
      description: data.metadata.description,
      faviconUrl: data.metadata.faviconUrl,
      thumbnailUrl: data.oembed?.thumbnailUrl ?? data.metadata.imageUrl ?? null,
      thumbnailWidth: data.oembed?.thumbnailWidth ?? null,
      thumbnailHeight: data.oembed?.thumbnailHeight ?? null,
      embedType: data.oembed?.providerName ?? null,
      oembedType: data.oembed?.type ?? null,
      embedUrl: data.oembed?.embedUrl ?? null,
      embedHtml: data.oembed?.embedHtml ?? null,
      embedWidth: data.oembed?.embedWidth ?? null,
      embedHeight: data.oembed?.embedHeight ?? null,
    }
  } catch {
    return null
  }
}

const PROVIDER_LABELS: Record<string, string> = {
  youtube: "YouTube",
  spotify: "Spotify",
}

function getEmbedLabel(node: Node): string {
  if (node.attrs.providerName === "oembed") {
    return node.attrs.embedType || ""
  }
  return PROVIDER_LABELS[node.attrs.providerName] ?? "Website"
}

class EmbedEditView {
  node: Node
  view: EditorView
  getPos: NodeViewRendererProps["getPos"]
  dom: HTMLElement

  private urlInput: HTMLInputElement | null = null
  private previewContainer: HTMLElement | null = null
  private loadingContainer: HTMLElement | null = null
  private choiceContainer: HTMLElement | null = null
  private isLoading = false
  private pendingChoice = false
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null

  constructor(
    node: Node,
    view: EditorView,
    getPos: NodeViewRendererProps["getPos"],
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos

    this.dom = document.createElement("figure")
    this.dom.setAttribute("data-type", "embed")
    this.dom.classList.add("embed-editor")
    this.dom.contentEditable = "false"
    this.updateLabel()

    if (node.attrs.url) {
      const needsEnrich = !node.attrs.thumbnailUrl && !node.attrs.embedUrl
      this.isLoading = needsEnrich
      this.renderPreview()
      if (needsEnrich) this.tryEnrich(node.attrs.providerName, node.attrs.url)
    } else {
      this.renderUrlInput()
    }
  }

  private renderUrlInput() {
    this.clearDom()

    const container = document.createElement("div")
    container.classList.add("embed-url-input-container")

    this.urlInput = document.createElement("input")
    this.urlInput.type = "text"
    this.urlInput.classList.add("embed-url-input")
    this.urlInput.placeholder =
      "Paste a URL (YouTube, Vimeo, Spotify, or any link...)"

    this.urlInput.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault()
        this.handleUrlSubmit(this.urlInput!.value.trim())
      }
      if (event.key === "Backspace" && !this.urlInput!.value) {
        event.preventDefault()
        this.deleteNode()
      }
      if (event.key === "Escape") {
        event.preventDefault()
        this.deleteNode()
      }
    })

    this.urlInput.addEventListener("paste", (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData("text/plain")?.trim()
      if (text) {
        setTimeout(() => {
          this.handleUrlSubmit(this.urlInput!.value.trim())
        }, 0)
      }
    })

    container.appendChild(this.urlInput)
    this.dom.appendChild(container)

    setTimeout(() => this.urlInput?.focus(), 50)
  }

  private handleUrlSubmit(url: string) {
    if (!url) return

    try {
      new URL(url)
    } catch {
      return
    }

    const parsed = parseEmbedUrl(url)

    const attrs: Record<string, string | null> = {
      url,
      providerName: parsed?.providerName ?? null,
      embedId: parsed?.embedId ?? null,
      embedType: parsed?.embedType ?? null,
      embedUrl: parsed?.embedUrl ?? null,
      thumbnailUrl: parsed?.thumbnailUrl ?? null,
      title: parsed?.title ?? null,
    }

    this.isLoading = true
    this.updateNodeAttrs(attrs)
    this.tryEnrich(parsed?.providerName ?? null, url)
  }

  private tryEnrich(_providerName: string | null, url: string) {
    enrichEmbedUrl(url).then(enriched => {
      this.isLoading = false
      if (enriched) {
        const mergedData = toEmbedData({ ...this.node.attrs, ...enriched })
        if (hasRichEmbed(mergedData) && !this.node.attrs.displayMode) {
          this.pendingChoice = true
        }
        this.updateNodeAttrs(enriched)
      } else {
        this.deleteNode()
      }
    })
  }

  private renderPreview() {
    this.clearDom()

    if (this.isLoading) {
      this.loadingContainer = document.createElement("div")
      this.loadingContainer.classList.add("embed-loading")
      const label = document.createElement("span")
      label.textContent = "Loading information..."
      this.loadingContainer.appendChild(label)
      this.dom.appendChild(this.loadingContainer)
      return
    }

    if (this.pendingChoice) {
      this.renderDisplayChoice()
      return
    }

    this.previewContainer = document.createElement("div")
    this.previewContainer.classList.add("embed-preview")

    renderEditorPreview(this.previewContainer, toEmbedData(this.node.attrs))

    this.dom.appendChild(this.previewContainer)
  }

  private renderDisplayChoice() {
    this.clearDom()

    const container = document.createElement("div")
    container.classList.add("embed-display-choice")

    const title = this.node.attrs.title as string | null
    const url = this.node.attrs.url as string | null
    if (title || url) {
      const meta = document.createElement("div")
      meta.classList.add("embed-display-choice-meta")
      if (title) {
        const titleEl = document.createElement("span")
        titleEl.classList.add("embed-display-choice-title")
        titleEl.textContent = title
        meta.appendChild(titleEl)
      }
      if (url) {
        const urlEl = document.createElement("span")
        urlEl.classList.add("embed-display-choice-url")
        urlEl.textContent = url
        meta.appendChild(urlEl)
      }
      container.appendChild(meta)
    }

    const label = document.createElement("span")
    label.classList.add("embed-display-choice-label")
    label.textContent = "Display as..."
    container.appendChild(label)

    const buttons = document.createElement("div")
    buttons.classList.add("embed-display-choice-buttons")

    const embedBtn = this.makeChoiceButton("Interactive", "globe")
    embedBtn.addEventListener("click", (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      this.pendingChoice = false
      this.removeOutsideClickHandler()
      this.updateNodeAttrs({ displayMode: "interactive" })
    })

    const cardBtn = this.makeChoiceButton("Card", "newspaper")
    cardBtn.addEventListener("click", (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      this.pendingChoice = false
      this.removeOutsideClickHandler()
      this.updateNodeAttrs({ displayMode: "card" })
    })

    buttons.appendChild(embedBtn)
    buttons.appendChild(cardBtn)
    container.appendChild(buttons)
    this.dom.appendChild(container)
    this.choiceContainer = container

    this.removeOutsideClickHandler()
    this.outsideClickHandler = (event: MouseEvent) => {
      if (!this.choiceContainer?.contains(event.target as HTMLElement)) {
        this.removeOutsideClickHandler()
        this.resolveChoiceDefault()
      }
    }
    document.addEventListener("mousedown", this.outsideClickHandler)
  }

  private makeChoiceButton(text: string, iconName: string): HTMLButtonElement {
    const btn = document.createElement("button")
    btn.type = "button"
    const iconSpan = document.createElement("span")
    iconSpan.setAttribute("aria-hidden", "true")
    iconSpan.innerHTML = `<svg width="16" height="16" stroke-width="2" stroke-linecap="round" fill="none" stroke="currentColor"><use href="#${iconName}"></use></svg>`
    const textSpan = document.createElement("span")
    textSpan.textContent = text
    btn.appendChild(iconSpan)
    btn.appendChild(textSpan)
    return btn
  }

  private removeOutsideClickHandler() {
    if (this.outsideClickHandler) {
      document.removeEventListener("mousedown", this.outsideClickHandler)
      this.outsideClickHandler = null
    }
  }

  private resolveChoiceDefault() {
    if (!this.pendingChoice) return
    this.pendingChoice = false
    this.removeOutsideClickHandler()
    this.updateNodeAttrs({ displayMode: "interactive" })
  }

  private clearDom() {
    this.removeOutsideClickHandler()
    while (this.dom.firstChild) {
      this.dom.removeChild(this.dom.firstChild)
    }
    this.urlInput = null
    this.previewContainer = null
    this.loadingContainer = null
    this.choiceContainer = null
  }

  private updateNodeAttrs(attrs: Record<string, unknown>) {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        ...attrs,
      }),
    )
  }

  private deleteNode() {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize))
    this.view.focus()
  }

  private attrsChanged(prev: Node, next: Node): boolean {
    const keys = [
      "url",
      "providerName",
      "embedId",
      "embedType",
      "embedUrl",
      "embedHtml",
      "oembedType",
      "thumbnailUrl",
      "faviconUrl",
      "title",
      "description",
      "embedWidth",
      "embedHeight",
      "thumbnailWidth",
      "thumbnailHeight",
      "displayMode",
    ]
    return keys.some(k => prev.attrs[k] !== next.attrs[k])
  }

  update(node: Node) {
    if (node.type !== this.node.type) return false

    const changed = this.attrsChanged(this.node, node)
    this.node = node

    if (changed) this.updateLabel()

    const hasContent =
      this.previewContainer ?? this.loadingContainer ?? this.choiceContainer
    if (node.attrs.url && !hasContent) {
      this.renderPreview()
    } else if (node.attrs.url && hasContent && changed) {
      this.renderPreview()
    } else if (!node.attrs.url && !this.urlInput) {
      this.renderUrlInput()
    }

    return true
  }

  private updateLabel() {
    this.dom.setAttribute("data-label", getEmbedLabel(this.node))
  }

  selectNode() {
    this.dom.classList.add("ProseMirror-selectednode")
    this.dom.setAttribute("data-selected", "true")
  }

  deselectNode() {
    this.dom.classList.remove("ProseMirror-selectednode")
    this.dom.removeAttribute("data-selected")
    this.resolveChoiceDefault()
  }

  stopEvent(event: Event) {
    const target = event.target as HTMLElement
    if (this.urlInput?.contains(target)) return true
    if (this.choiceContainer?.contains(target)) return true
    return false
  }

  ignoreMutation() {
    return true
  }

  destroy() {
    this.removeOutsideClickHandler()
  }
}

const embedPasteKey = new PluginKey("embedPaste")

export function getEditEmbed() {
  return EmbedNode.extend({
    addNodeView() {
      return (props: NodeViewRendererProps) =>
        new EmbedEditView(props.node, props.editor.view, props.getPos)
    },

    addProseMirrorPlugins() {
      const embedType = this.type
      return [
        new Plugin({
          key: embedPasteKey,
          props: {
            handlePaste(view: EditorView, event: ClipboardEvent) {
              const { state } = view
              const { selection } = state
              const { $from, empty } = selection

              if (!empty) return false
              if ($from.depth !== 1) return false
              if ($from.parent.type.name !== "paragraph") return false
              if (!isNodeEmpty($from.parent)) return false

              const text = event.clipboardData?.getData("text/plain")?.trim()
              if (!text) return false

              try {
                new URL(text)
              } catch {
                return false
              }

              const parsed = parseEmbedUrl(text)
              const attrs = {
                url: text,
                providerName: parsed?.providerName ?? null,
                embedId: parsed?.embedId ?? null,
                embedType: parsed?.embedType ?? null,
                embedUrl: parsed?.embedUrl ?? null,
                thumbnailUrl: parsed?.thumbnailUrl ?? null,
                title: parsed?.title ?? null,
              }

              const pos = $from.before(1)
              const node = embedType.create(attrs)
              const tr = state.tr.replaceWith(
                pos,
                pos + $from.parent.nodeSize,
                node,
              )
              view.dispatch(tr)

              return true
            },
          },
        }),
      ]
    },
  })
}
